// @flow
import React, { useCallback } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import ToggleButton from '@material-ui/lab/ToggleButton';
import IconButton from '@material-ui/core/IconButton';
import PowerOffIcon from '@material-ui/icons/PowerSettingsNew';
import SettingsIcon from '@material-ui/icons/Settings';
import PlaneIcon from '@material-ui/icons/Flight';
import WarningIcon from '@material-ui/icons/Warning';
import type { Status, Runways } from '../../types';
import * as constants from '../../constants/appConstants';
import firebase from '../../services/firebase';
import ConfigModel from '../../models/Config';
import NowFeeder from '../ui/NowFeeder';
import CarIconContainer from './containers/CarIconContainer';
import StatusBar from './StatusBar';
import Select from './Select';
import Time from './Time';

const useSection = (section, activeSection, setActiveSection) => {
    const toggler = useCallback(() => setActiveSection(activeSection === section ? null : section), [section, activeSection, setActiveSection]);

    return {
        section,
        isActive: activeSection === section,
        toggler
    };
};

const useStyles = makeStyles(theme => ({
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
        position: 'relative',
        backgroundColor: theme.palette.background.paper
    },
    controls: {
        marginRight: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        flexGrow: 1,
    },
    controlIcons: {
        display: 'flex',
        alignItems: 'center',
    },
    spacingRight2: {
        marginRight: theme.spacing(2),
    },
    spacingRight4: {
        marginRight: theme.spacing(4),
    },
    spacingRight1ch: {
        marginRight: '.5ch',
    },
}));

export type Props = $ReadOnly<{
    activeSection: null | typeof constants.SECTION,
    status: Status,
    runways: Runways,
    planeCount: number,
    userName?: string,

    setActiveSection: (null | typeof constants.SECTION) => void
}>;

function Navbar({ activeSection, status, runways, planeCount, userName, setActiveSection }: Props) {
    const classes = useStyles();
    const optionsSection = useSection(constants.SECTION.options, activeSection, setActiveSection);
    const zonesSection = useSection(constants.SECTION.zones, activeSection, setActiveSection);
    const targetsSection = useSection(constants.SECTION.targets, activeSection, setActiveSection);
    const alertsSection = useSection(constants.SECTION.alerts, activeSection, setActiveSection);

    const logout = () => firebase.auth().signOut();

    const visibility = status.visibility && status.visibility.value || ConfigModel.constants.VISIBILITY_NVO;
    const setVisibility = (e, value) => value && ConfigModel.setStatus('visibility', value);

    const setStatus = (key) => (e) => ConfigModel.setStatus(key, e.target.value);

    return (
        <React.Fragment>
            <AppBar className={classes.appBar} position="static" color="default">
                <Toolbar style={{ paddingRight: 8 }}>
                    <div className={classes.controls}>

                        <ToggleButton
                            size="small"
                            color="primary"
                            className={classes.spacingRight2}
                            value={optionsSection.section}
                            selected={optionsSection.isActive}
                            onChange={optionsSection.toggler}
                        >
                            <SettingsIcon />
                        </ToggleButton>

                        <ToggleButton
                            size="small"
                            className={classes.spacingRight2}
                            value={zonesSection.section}
                            selected={zonesSection.isActive}
                            onChange={zonesSection.toggler}
                        >
                            <FormattedMessage
                                id="navbar.button.zones"
                                defaultMessage="Zones"
                            />
                        </ToggleButton>

                        <ToggleButton
                            size="small"
                            className={classes.spacingRight4}
                            value={targetsSection.section}
                            selected={targetsSection.isActive}
                            onChange={targetsSection.toggler}
                        >
                            <span className={classnames(classes.controlIcons, classes.spacingRight2)}>
                                <NowFeeder>
                                    {(now) => <CarIconContainer iconClass={classes.spacingRight1ch} now={now} />}
                                </NowFeeder>
                            </span>
                            <span className={classes.controlIcons}>
                                <PlaneIcon className={classes.spacingRight1ch} /> {planeCount}
                            </span>
                        </ToggleButton>

                        <ToggleButton
                            size="small"
                            className={classes.spacingRight4}
                            value={alertsSection.section}
                            selected={alertsSection.isActive}
                            onChange={alertsSection.toggler}
                        >
                            <WarningIcon />
                        </ToggleButton>

                        <ToggleButtonGroup
                            size="small"
                            className={classes.spacingRight4}
                            value={visibility}
                            exclusive
                            onChange={setVisibility}
                        >
                            <ToggleButton value={ConfigModel.constants.VISIBILITY_LVO}>
                                <FormattedMessage
                                    id="navbar.button.lvo"
                                    defaultMessage="LVO"
                                />
                            </ToggleButton>
                            <ToggleButton value={ConfigModel.constants.VISIBILITY_NVO}>
                                <FormattedMessage
                                    id="navbar.button.nvo"
                                    defaultMessage="NVO"
                                />
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <Select
                            id="arrival"
                            className={classes.spacingRight2}
                            label={
                                <FormattedMessage
                                    id="navbar.select.arrival"
                                    defaultMessage="ARR"
                                />
                            }
                            value={status.arrival && status.arrival.value}
                            options={runways.arrival}
                            onChange={setStatus('arrival')}
                        />
                        <Select
                            id="departure"
                            className={classes.spacingRight4}
                            label={
                                <FormattedMessage
                                    id="navbar.select.departure"
                                    defaultMessage="DEP"
                                />
                            }
                            value={status.departure && status.departure.value}
                            options={runways.departure}
                            onChange={setStatus('departure')}
                        />

                        <Time />
                    </div>

                    {userName &&
                        <span>
                            {userName}
                        </span>
                    }

                    <IconButton
                        aria-label="account of current user"
                        aria-controls="menu-appbar"
                        aria-haspopup="true"
                        color="inherit"
                        onClick={logout}
                    >
                        <PowerOffIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>
            <StatusBar status={status} />
        </React.Fragment>
    );
}

export default Navbar;